import React, { useState, useEffect, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import Select from "react-select";
import Cookies from "js-cookie";
import {
  getTeacherCumulativeDashboardOverview,
  getTeacherCumulativeDashboardCourseOverview,
  getTeacherCumulativeDashboardBatchOverview,
} from "../../helper/apicalls/dashboard";
import BarChart from "../common/charts/BarChart";
import LineChart from "../common/charts/LineChart";
import PieChart from "../common/charts/PieChart";
import AreaChart from "../common/charts/AreaChart";
import PolarAreaChart from "../common/charts/PolarAreaChart";
import ChartCard from "../common/charts/ChartCard";
import BatchDashboardView from "../institution/BatchDashboardView";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TooltipText from "../common/tooltipText";
import GlobalLoader from "../common/GlobalLoader";
import "../../styles/common/dashboardPage.scss";
import "../../styles/common/dashboardOverviewSection.scss";
import "../../styles/teacher/teacherDashboardPage.scss";

const TeacherDashboardPage = () => {
  const { setHeaderName, setIsShowSearch } = useOutletContext?.() || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const chartPalette = useMemo(
    () => ({
      solid: {
        orange: "rgba(252, 164, 59, 0.85)",
        mint: "rgba(133, 238, 173, 0.85)",
        lime: "rgba(212, 249, 45, 0.85)",
        yellow: "rgba(250, 238, 112, 0.85)",
        slate: "rgba(148, 163, 184, 0.65)",
      },
      hover: {
        orange: "rgba(252, 164, 59, 0.95)",
        mint: "rgba(133, 238, 173, 0.95)",
        lime: "rgba(212, 249, 45, 0.95)",
        yellow: "rgba(250, 238, 112, 0.95)",
        slate: "rgba(148, 163, 184, 0.85)",
      },
      soft: {
        orange: "rgba(252, 164, 59, 0.16)",
        mint: "rgba(133, 238, 173, 0.16)",
        lime: "rgba(212, 249, 45, 0.16)",
        yellow: "rgba(250, 238, 112, 0.16)",
        slate: "rgba(148, 163, 184, 0.16)",
      },
      accents: [
        "rgba(252, 164, 59, 0.85)",
        "rgba(133, 238, 173, 0.85)",
        "rgba(212, 249, 45, 0.85)",
        "rgba(250, 238, 112, 0.85)",
        "rgba(148, 163, 184, 0.65)",
      ],
      accentsHover: [
        "rgba(252, 164, 59, 0.95)",
        "rgba(133, 238, 173, 0.95)",
        "rgba(212, 249, 45, 0.95)",
        "rgba(250, 238, 112, 0.95)",
        "rgba(148, 163, 184, 0.85)",
      ],
    }),
    [],
  );

  const sliceColors = (count) =>
    Array.from(
      { length: count },
      (_, i) => chartPalette.accents[i % chartPalette.accents.length],
    );

  const sliceHoverColors = (count) =>
    Array.from(
      { length: count },
      (_, i) => chartPalette.accentsHover[i % chartPalette.accentsHover.length],
    );

  const [overallData, setOverallData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);

  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");

  const [courseData, setCourseData] = useState(null);
  const [batchData, setBatchData] = useState(null);

  const toNum = (v, fallback = 0) => {
    if (v === undefined || v === null) return fallback;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const formatSeconds = (seconds) => {
    const raw = Number(seconds);
    if (!Number.isFinite(raw) || raw <= 0) return "0m";

    const totalSeconds = Math.floor(raw);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const colorFromKey = undefined;

  useEffect(() => {
    if (setHeaderName) setHeaderName("Dashboard");
    if (setIsShowSearch) setIsShowSearch(false);
  }, [setHeaderName, setIsShowSearch]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const token = Cookies.get("teacher_token");
      if (!token) return;

      setLoading(true);
      try {
        const res = await getTeacherCumulativeDashboardOverview(token);
        setOverallData(res);
        // Map courses correctly from res.courses
        const mappedCourses = (res?.courses || []).map((c) => ({
          key: c.course_key, // This is CL2, CL19 etc.
          name: c.course_name,
          id: c.course_id,
        }));
        setCourses(mappedCourses);
      } catch (err) {
        console.error("Error fetching initial teacher dashboard data", err);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchCourseData = async () => {
      if (!selectedCourse) {
        setCourseData(null);
        setBatches([]);
        setSelectedBatch("");
        setBatchData(null);
        return;
      }
      const token = Cookies.get("teacher_token");
      setLoading(true);
      try {
        const res = await getTeacherCumulativeDashboardCourseOverview(
          token,
          selectedCourse,
        );
        setCourseData(res);
        const courseBatches = (res?.batches || []).map((b) => ({
          id: b.batch_id,
          name: b.batch_name,
        }));
        setBatches(courseBatches);
      } catch (err) {
        console.error("Error fetching teacher course data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCourseData();
  }, [selectedCourse, overallData, courses]);

  useEffect(() => {
    const fetchBatchData = async () => {
      if (!selectedBatch) {
        setBatchData(null);
        return;
      }
      const token = Cookies.get("teacher_token");
      setLoading(true);
      try {
        const res = await getTeacherCumulativeDashboardBatchOverview(
          token,
          selectedBatch,
        );
        setBatchData(res);
      } catch (err) {
        console.error("Error fetching teacher batch data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBatchData();
  }, [selectedBatch]);

  const activeData = batchData || courseData || overallData;

  const overallSummary = useMemo(() => {
    const o = activeData?.overall || null;

    const totalStudents =
      toNum(o?.total_students, null) ??
      toNum(activeData?.total_students, null) ??
      toNum(batchData?.total_students, null) ??
      0;

    const totalBatches = Array.isArray(activeData?.batches)
      ? activeData.batches.length
      : toNum(activeData?.total_batches, 0);

    const totalCourses = Array.isArray(activeData?.courses)
      ? activeData.courses.length
      : 0;

    return {
      totalStudents,
      totalBatches,
      totalCourses,
      totalTestPages: toNum(
        o?.total_test_pages,
        toNum(activeData?.total_test_pages, 0),
      ),
      totalQuizPages: toNum(
        o?.total_quiz_pages,
        toNum(activeData?.total_quiz_pages, 0),
      ),
      totalHomeworks: toNum(
        o?.total_homework_slots_assigned,
        toNum(
          activeData?.total_homework_assigned_slots,
          toNum(
            activeData?.total_homework_count,
            toNum(activeData?.total_homework_assigned, 0),
          ),
        ),
      ),
      totalSubjectPages: toNum(
        o?.total_subject_pages,
        toNum(activeData?.total_subject_pages, 0),
      ),
      totalSubjectCompleted: toNum(
        o?.total_subject_slots_completed,
        toNum(activeData?.total_subject_pages_completed, 0),
      ),
      totalHomeworkAssigned: toNum(
        o?.total_homework_slots_assigned,
        toNum(activeData?.total_homework_assigned_slots, 0),
      ),
      totalHomeworkCompleted: toNum(
        o?.total_homework_slots_completed,
        toNum(activeData?.total_homework_completed_slots, 0),
      ),
      totalQuizAssigned: toNum(
        o?.total_quiz_assigned,
        toNum(activeData?.total_quiz_assigned, 0),
      ),
      totalQuizCompleted: toNum(
        o?.total_quiz_completed_slots,
        toNum(activeData?.total_quiz_completed_slots, 0),
      ),
      totalTestAssigned: toNum(
        o?.total_test_assigned,
        toNum(activeData?.total_test_assigned, 0),
      ),
      totalTestCompleted: toNum(
        o?.total_test_completed_slots,
        toNum(activeData?.total_test_completed_slots, 0),
      ),
    };
  }, [activeData, batchData, toNum]);

  const metrics = useMemo(() => {
    if (!activeData) return { students: 0, batches: 0, courses: 0 };

    if (overallData && !courseData && !batchData) {
      return {
        students:
          overallData.courses?.reduce(
            (acc, c) => acc + toNum(c.total_students, 0),
            0,
          ) || 0,
        batches: overallData.batches?.length || 0,
        courses: overallData.courses?.length || 0,
      };
    }

    if (courseData && !batchData) {
      return {
        students:
          courseData.batches?.reduce(
            (acc, b) => acc + toNum(b.total_students, 0),
            0,
          ) ||
          courseData.total_students ||
          0,
        batches: courseData.batches?.length || courseData.total_batches || 0,
        courses: 1,
      };
    }

    if (batchData) {
      return {
        students: batchData.total_students || 0,
        batches: 1,
        courses: 1,
      };
    }

    return { students: 0, batches: 0, courses: 0 };
  }, [activeData, overallData, courseData, batchData]);

  const leaderboardData = useMemo(() => {
    return activeData?.stats || null;
  }, [activeData]);

  const leaderboardCharts = useMemo(() => {
    if (!leaderboardData) return null;

    const getPolarOptions = () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 12,
            padding: 15,
            usePointStyle: true,
            pointStyle: "circle",
            font: { size: 11, weight: "500" },
          },
        },
        tooltip: {
          backgroundColor: "#1e293b",
          padding: 12,
          titleFont: { size: 13 },
          bodyFont: { size: 12 },
        },
      },
      scales: {
        r: {
          grid: { color: "rgba(148, 163, 184, 0.22)" },
          angleLines: { color: "rgba(148, 163, 184, 0.22)" },
          ticks: { display: false },
        },
      },
    });

    const getPieOptions = () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 12,
            padding: 15,
            usePointStyle: true,
            pointStyle: "circle",
            font: { size: 11, weight: "500" },
          },
        },
        tooltip: {
          backgroundColor: "#1e293b",
          padding: 12,
          callbacks: {
            label: (context) => ` ${context.label}: ${context.raw}%`,
          },
        },
      },
    });

    const getColorListForLabels = (labels, seed = 0) => {
      const arr = Array.isArray(labels) ? labels : [];
      return arr.map((lbl, idx) => {
        const pick =
          chartPalette.accents[(seed + idx) % chartPalette.accents.length];
        if (typeof pick !== "string") return chartPalette.solid.mint;
        const m = pick.match(/^rgba\((\s*\d+\s*),(\s*\d+\s*),(\s*\d+\s*),/i);
        if (!m) return pick;
        return `rgba(${m[1]}, ${m[2]}, ${m[3]}, 0.85)`;
      });
    };

    const getChartData = (list, key, seed = 0) => {
      const sorted = list || [];
      const labels = sorted.map(
        (item) => item.batch_name || item.student_name || "N/A",
      );
      return {
        labels,
        datasets: [
          {
            data: sorted.map((item) => {
              if (key === "subject_completion_percentage") {
                return toNum(
                  item.completion_percentage ??
                    item.subject_completion_percentage,
                  0,
                );
              }
              if (key === "homework_user_completion_rate") {
                return toNum(
                  item.homework_user_completion_rate ??
                    item.total_homework_completed,
                  0,
                );
              }
              if (key === "quiz_user_completion_rate") {
                return toNum(
                  item.quiz_user_completion_rate ?? item.quiz_completion_rate,
                  0,
                );
              }
              if (key === "test_user_completion_rate") {
                return toNum(
                  item.test_user_completion_rate ?? item.test_completion_rate,
                  0,
                );
              }
              if (key === "total_coins") {
                return toNum(
                  item.total_coins ?? item.coins ?? item.total_coin ?? 0,
                  0,
                );
              }
              if (key === "total_xp") {
                return toNum(
                  item.total_xp ?? item.xp ?? item.total_experience ?? 0,
                  0,
                );
              }
              if (key === "total_time_spent_seconds") {
                const seconds = toNum(
                  item.total_time_spent_seconds ??
                    item.time_spent_seconds ??
                    item.total_time_spent ??
                    0,
                  0,
                );
                return Math.round((seconds / 60) * 100) / 100;
              }
              return toNum(item[key], 0);
            }),
            backgroundColor: getColorListForLabels(labels, seed),
            borderWidth: 1,
            borderColor: "#fff",
          },
        ],
      };
    };

    return {
      batch: {
        data: getChartData(
          leaderboardData.batch_completion?.leaderboard,
          "subject_completion_percentage",
          0,
        ),
        options: getPolarOptions(),
      },
      activeRecall: {
        data: getChartData(
          leaderboardData.active_recall?.leaderboard,
          "active_recall_completion_rate",
          30,
        ),
        options: getPolarOptions(),
      },
      adaptiveLearning: {
        data: getChartData(
          leaderboardData.adaptive_learning?.leaderboard,
          "adaptive_completion_rate",
          60,
        ),
        options: getPolarOptions(),
      },
      homework: {
        data: getChartData(
          leaderboardData.homework?.leaderboard,
          "homework_user_completion_rate",
          90,
        ),
        options: getPieOptions(),
      },
      quiz: {
        data: getChartData(
          leaderboardData.quizzes?.leaderboard,
          "quiz_user_completion_rate",
          180,
        ),
        options: getPolarOptions(),
      },
      test: {
        data: getChartData(
          leaderboardData.tests?.leaderboard,
          "test_user_completion_rate",
          270,
        ),
        options: getPieOptions(),
      },
      coins: {
        data: getChartData(
          leaderboardData.coins?.leaderboard,
          "total_coins",
          360,
        ),
        options: getPieOptions(),
      },
      xp: {
        data: getChartData(leaderboardData.xp?.leaderboard, "total_xp", 450),
        options: getPolarOptions(),
      },
      timeSpent: {
        data: getChartData(
          leaderboardData.time_spent?.leaderboard,
          "total_time_spent_seconds",
          540,
        ),
        options: getPieOptions(),
      },
    };
  }, [leaderboardData]);

  const chartsData = useMemo(() => {
    const dataToMap =
      batchData?.students || courseData?.batches || overallData?.batches;
    if (!dataToMap || !Array.isArray(dataToMap)) return null;

    const sortedData = [...dataToMap].sort((a, b) => {
      const valA = toNum(
        a.completion_percentage ?? a.subject_completion_percentage,
        0,
      );
      const valB = toNum(
        b.completion_percentage ?? b.subject_completion_percentage,
        0,
      );
      return valB - valA;
    });

    const labels = sortedData.map(
      (item) => item.student_name || item.batch_name || "N/A",
    );

    return {
      subject: {
        labels,
        datasets: [
          {
            label: "Subject Completion (%)",
            data: sortedData.map((item) =>
              toNum(
                item.completion_percentage ??
                  item.subject_completion_percentage,
                0,
              ),
            ),
            borderColor: chartPalette.solid.mint,
            backgroundColor: chartPalette.soft.mint,
            tension: 0.35,
            fill: true,
            pointRadius: 2,
            pointBackgroundColor: chartPalette.solid.mint,
            pointHoverBackgroundColor: chartPalette.hover.mint,
          },
        ],
      },
      test: {
        labels,
        datasets: [
          {
            label: "Test Completion (%)",
            data: sortedData.map((item) =>
              toNum(
                item.test_user_completion_rate ?? item.test_completion_rate,
                0,
              ),
            ),
            borderColor: chartPalette.solid.orange,
            backgroundColor: chartPalette.soft.orange,
            tension: 0.35,
            fill: true,
            pointRadius: 2,
            pointBackgroundColor: chartPalette.solid.orange,
            pointHoverBackgroundColor: chartPalette.hover.orange,
          },
        ],
      },
      homework: {
        labels,
        datasets: [
          {
            label: "Homework Completion (%)",
            data: sortedData.map((item) =>
              toNum(
                item.homework_user_completion_rate ??
                  item.total_homework_completed,
                0,
              ),
            ),
            backgroundColor: chartPalette.solid.lime,
            hoverBackgroundColor: chartPalette.hover.lime,
            borderRadius: 6,
          },
        ],
      },
      quiz: {
        labels,
        datasets: [
          {
            label: "Quiz Completion (%)",
            data: sortedData.map((item) =>
              toNum(
                item.quiz_user_completion_rate ?? item.quiz_completion_rate,
                0,
              ),
            ),
            borderColor: chartPalette.solid.yellow,
            backgroundColor: chartPalette.soft.yellow,
            tension: 0.35,
            fill: true,
            pointRadius: 2,
            pointBackgroundColor: chartPalette.solid.yellow,
            pointHoverBackgroundColor: chartPalette.hover.yellow,
          },
        ],
      },
    };
  }, [batchData, chartPalette, courseData, overallData, toNum]);

  const batchWiseCourseCompletionChart = useMemo(() => {
    const list = activeData?.course_completion_batch_wise;
    if (!Array.isArray(list) || list.length === 0) return null;

    const studentCompletionByBatchId = new Map(
      (Array.isArray(activeData?.batches) ? activeData.batches : []).map(
        (b) => [b?.batch_id, toNum(b?.student_avg_completion_percentage, 0)],
      ),
    );

    const sorted = [...list].sort(
      (a, b) =>
        toNum(b.teacher_course_completion_percentage, 0) -
        toNum(a.teacher_course_completion_percentage, 0),
    );

    const labels = sorted.map((item) => {
      const course = item?.course_name || "";
      const batch = item?.batch_name || "";
      const label = [course, batch].filter(Boolean).join(" - ");
      return label || "N/A";
    });

    // const teacherColor = colorFromKey(
    //   "Teacher Course Completion (Batch-wise)",
    //   0.82,
    //   72,
    //   45,
    //   45,
    // );
    // const studentColor = colorFromKey(
    //   "Student Avg Completion (Batch-wise)",
    //   0.82,
    //   72,
    //   45,
    //   200,
    // );

    return {
      data: {
        labels,
        datasets: [
          {
            label: "Teacher Completion (%)",
            data: sorted.map((item) =>
              toNum(item.teacher_course_completion_percentage, 0),
            ),
            backgroundColor: chartPalette.solid.orange,
            hoverBackgroundColor: chartPalette.hover.orange,
            borderRadius: 8,
          },
          {
            label: "Student Completion (%)",
            data: sorted.map((item) =>
              toNum(studentCompletionByBatchId.get(item?.batch_id), 0),
            ),
            backgroundColor: chartPalette.solid.mint,
            hoverBackgroundColor: chartPalette.hover.mint,
            borderRadius: 8,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true, position: "bottom" },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const v = toNum(ctx.raw, 0);
                const ds = ctx?.dataset?.label ? `${ctx.dataset.label}: ` : "";
                return ` ${ds}${v}%`;
              },
            },
          },
        },
        scales: {
          x: { ticks: { autoSkip: false, maxRotation: 45, minRotation: 0 } },
          y: { beginAtZero: true, max: 100 },
        },
      },
    };
  }, [activeData, chartPalette, toNum]);

  const batchWiseTimeSpentChart = useMemo(() => {
    const list =
      activeData?.batches ||
      activeData?.time_spent_batch_wise ||
      activeData?.batch_wise_time_spent ||
      activeData?.batch_time_spent ||
      activeData?.batch_wise_time_spent_seconds ||
      null;

    if (!Array.isArray(list) || list.length === 0) return null;

    const sorted = [...list].sort(
      (a, b) =>
        toNum(b.total_time_spent_seconds, 0) -
        toNum(a.total_time_spent_seconds, 0),
    );

    const labels = sorted.map((item) => {
      const course = item?.course_name || "";
      const batch = item?.batch_name || "";
      const label = [course, batch].filter(Boolean).join(" - ");
      return label || item?.batch_id || "N/A";
    });

    return {
      data: {
        labels,
        datasets: [
          {
            label: "Time Spent (hours)",
            data: sorted.map(
              (item) =>
                +(toNum(item.total_time_spent_seconds, 0) / 3600).toFixed(2),
            ),
            backgroundColor: chartPalette.solid.yellow,
            hoverBackgroundColor: chartPalette.hover.yellow,
            borderRadius: 12,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const idx = ctx.dataIndex;
                const seconds = toNum(
                  sorted?.[idx]?.total_time_spent_seconds,
                  0,
                );
                return ` ${formatSeconds(seconds)} (${seconds}s)`;
              },
            },
          },
        },
        scales: {
          x: { ticks: { autoSkip: false, maxRotation: 45, minRotation: 0 } },
          y: { beginAtZero: true, title: { display: true, text: "Hours" } },
        },
      },
    };
  }, [leaderboardData, sliceColors, sliceHoverColors, toNum]);

  const hasPerformanceCharts =
    !!chartsData ||
    !!batchWiseCourseCompletionChart ||
    !!batchWiseTimeSpentChart;

  const courseOptions = useMemo(
    () => courses.map((c) => ({ value: c.key, label: c.name })),
    [courses],
  );

  const batchOptions = useMemo(
    () => batches.map((b) => ({ value: b.id, label: b.name })),
    [batches],
  );

  const toMinutes = (seconds) => {
    const v = Number(seconds);
    if (!Number.isFinite(v)) return 0;
    return Math.round((v / 60) * 100) / 100;
  };

  return (
    <div className="dashboard-page teacher-dashboard-page" style={{ paddingBottom: 24 }}>
      {loading && <GlobalLoader visible={loading} />}
      {!!error && <div className="dashboard-error">{error}</div>}

      <div className="dashboard-filters-row teacher-dashboard-filters-row">
        <div className="filter-item teacher-dashboard-filter-item">
          <label className="teacher-dashboard-filter-label">Course</label>
          <Select
            options={courseOptions}
            value={
              courseOptions.find((o) => o.value === selectedCourse) || null
            }
            onChange={(opt) => {
              setSelectedCourse(opt?.value || "");
              setSelectedBatch("");
              setBatches([]);
              setBatchData(null);
            }}
            placeholder="Select Course"
            isClearable
          />
        </div>

        <div className="filter-item teacher-dashboard-filter-item">
          <label className="teacher-dashboard-filter-label">Batch</label>
          <Select
            key={selectedCourse || "__no_course__"}
            options={batchOptions}
            value={batchOptions.find((o) => o.value === selectedBatch) || null}
            onChange={(opt) => setSelectedBatch(opt?.value || "")}
            placeholder="Select Batch"
            isDisabled={!selectedCourse}
            isClearable
          />
        </div>
      </div>

      <section className="dashboard-overview-section">
        <h2>Overview</h2>
        <div className="overview-grid">
          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Total Students"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{overallSummary.totalStudents}</strong>
              </div>
              <GroupsOutlinedIcon className="overview-card-icon" />
            </div>
          </div>

          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Total Courses"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{overallSummary.totalCourses}</strong>
              </div>
              <MenuBookIcon className="overview-card-icon" />
            </div>
          </div>

          <div className="card overview-card">
            <div className="overview-card-content">
              <div className="overview-card-text">
                <TooltipText
                  text="Total Batches"
                  className="overview-card-label"
                  fullWidth
                />
                <strong>{overallSummary.totalBatches}</strong>
              </div>
              <SchoolOutlinedIcon
                className="overview-card-icon"
                sx={{ color: "#FCA43B !important" }}
              />
            </div>
          </div>
        </div>
      </section>

      {selectedBatch && batchData && (
        <section className="teacher-dashboard-section-spaced">
          <BatchDashboardView
            stats={batchData?.stats}
            overall={batchData?.overall}
            subjectWiseStats={batchData?.subject_wise_stats}
            studentHierarchyCompletion={batchData?.student_hierarchy_completion}
            teacherHierarchyCompletion={batchData?.teacher_hierarchy_completion}
            toMinutes={toMinutes}
            showOverview={false}
            showLeaderboards={false}
          />
        </section>
      )}

      {hasPerformanceCharts && (
        <section className="dashboard-course-section teacher-dashboard-section-spaced">
          <h2>Performance Breakdown</h2>
          <div className="dashboard-course-grid">
            {!!chartsData?.test && (
              <ChartCard title="Test Completion Trends (%)">
                <AreaChart
                  data={chartsData.test}
                  options={{
                    responsive: true,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { ticks: { autoSkip: false, maxLabelLength: 12 } },
                      y: { beginAtZero: true, max: 100 },
                    },
                  }}
                />
              </ChartCard>
            )}

            {!!chartsData?.homework && (
              <ChartCard title="Homework Completion Trends (%)">
                <BarChart
                  data={chartsData.homework}
                  options={{
                    responsive: true,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { ticks: { autoSkip: false, maxLabelLength: 12 } },
                      y: { beginAtZero: true, max: 100 },
                    },
                  }}
                />
              </ChartCard>
            )}

            {!!chartsData?.quiz && (
              <ChartCard title="Quiz Completion Trends (%)">
                <LineChart
                  data={chartsData.quiz}
                  options={{
                    responsive: true,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { ticks: { autoSkip: false, maxLabelLength: 12 } },
                      y: { beginAtZero: true, max: 100 },
                    },
                  }}
                />
              </ChartCard>
            )}

            {batchWiseCourseCompletionChart && (
              <ChartCard title="Course Completion by Batch (%)">
                <BarChart
                  data={batchWiseCourseCompletionChart.data}
                  options={batchWiseCourseCompletionChart.options}
                />
              </ChartCard>
            )}

            {batchWiseTimeSpentChart && (
              <ChartCard title="Time Spent by Batch">
                <BarChart
                  data={batchWiseTimeSpentChart.data}
                  options={batchWiseTimeSpentChart.options}
                />
              </ChartCard>
            )}
          </div>
        </section>
      )}

      {leaderboardCharts && (
        <section className="dashboard-course-section teacher-dashboard-section-spaced">
          <h2>Leaderboards</h2>
          <div className="dashboard-course-grid">
            <ChartCard title="Batch Completion (%)">
              <div className="chart-container teacher-dashboard-leaderboard-chart-container">
                <PolarAreaChart
                  data={leaderboardCharts.batch.data}
                  options={leaderboardCharts.batch.options}
                />
              </div>
            </ChartCard>

            {leaderboardCharts.activeRecall && (
              <ChartCard title="Active Recall Completion (%)">
                <div className="chart-container teacher-dashboard-leaderboard-chart-container">
                  <PolarAreaChart
                    data={leaderboardCharts.activeRecall.data}
                    options={leaderboardCharts.activeRecall.options}
                  />
                </div>
              </ChartCard>
            )}

            {leaderboardCharts.adaptiveLearning && (
              <ChartCard title="Adaptive Learning Completion (%)">
                <div className="chart-container teacher-dashboard-leaderboard-chart-container">
                  <PolarAreaChart
                    data={leaderboardCharts.adaptiveLearning.data}
                    options={leaderboardCharts.adaptiveLearning.options}
                  />
                </div>
              </ChartCard>
            )}

            <ChartCard title="Homework Completion (%)">
              <div className="chart-container teacher-dashboard-leaderboard-chart-container">
                <PieChart
                  data={leaderboardCharts.homework.data}
                  options={leaderboardCharts.homework.options}
                />
              </div>
            </ChartCard>

            <ChartCard title="Quiz Completion (%)">
              <div className="chart-container teacher-dashboard-leaderboard-chart-container">
                <PolarAreaChart
                  data={leaderboardCharts.quiz.data}
                  options={leaderboardCharts.quiz.options}
                />
              </div>
            </ChartCard>

            <ChartCard title="Test Completion (%)">
              <div className="chart-container teacher-dashboard-leaderboard-chart-container">
                {console.log(leaderboardCharts.test)}
                <PieChart
                  data={leaderboardCharts.test.data}
                  options={leaderboardCharts.test.options}
                />
              </div>
            </ChartCard>

            <ChartCard title="Coins">
              <div className="chart-container teacher-dashboard-leaderboard-chart-container">
                <PieChart
                  data={leaderboardCharts.coins.data}
                  options={leaderboardCharts.coins.options}
                />
              </div>
            </ChartCard>

            <ChartCard title="XP">
              <div className="chart-container teacher-dashboard-leaderboard-chart-container">
                <PolarAreaChart
                  data={leaderboardCharts.xp.data}
                  options={leaderboardCharts.xp.options}
                />
              </div>
            </ChartCard>

            <ChartCard title="Time Spent (min)">
              <div className="chart-container teacher-dashboard-leaderboard-chart-container">
                <BarChart
                  data={leaderboardCharts.timeSpent.data}
                  options={{
                    ...leaderboardCharts.timeSpent.options,
                    scales: { y: { beginAtZero: true } },
                  }}
                />
              </div>
            </ChartCard>
          </div>
        </section>
      )}
    </div>
  );
};

export default TeacherDashboardPage;
